/**
 * ModalGuardContext — prevents more than one app-level modal/dialog from being
 * open at the same time.
 *
 * Why this exists: when two modals stack, the user loses track of which one is
 * "in charge" — Esc / Enter / Tab become unpredictable and accessibility breaks.
 * The consensus pattern (Stripe, Linear, Notion, Gmail) is to never let two
 * modals coexist. This file is the single source of truth for that rule.
 *
 * Usage:
 *
 *   // In each modal component (so it registers itself when visible):
 *   useModalRegistration('feedback-widget', isOpen);
 *
 *   // At every trigger button (so it refuses when another modal is open):
 *   const { guard } = useModalGuard();
 *   onClick={() => guard(() => setShowFeedback(true), 'dialog')}
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

const ModalGuardContext = createContext(null);

export function ModalGuardProvider({ children }) {
  // We track ids in a Set behind a ref so register/unregister are stable and
  // don't trigger re-renders unless the cardinality actually changes.
  const idsRef = useRef(new Set());
  const [openCount, setOpenCount] = useState(0);

  const register = useCallback((id) => {
    if (!id || idsRef.current.has(id)) return;
    idsRef.current.add(id);
    setOpenCount(idsRef.current.size);
  }, []);

  const unregister = useCallback((id) => {
    if (!id || !idsRef.current.has(id)) return;
    idsRef.current.delete(id);
    setOpenCount(idsRef.current.size);
  }, []);

  const guard = useCallback((callback, label = 'dialog') => {
    if (idsRef.current.size > 0) {
      toast.info(`Please close the current ${label} before opening another.`);
      return false;
    }
    if (typeof callback === 'function') callback();
    return true;
  }, []);

  const value = useMemo(() => ({
    isAnyOpen: openCount > 0,
    openCount,
    register,
    unregister,
    guard,
  }), [openCount, register, unregister, guard]);

  return <ModalGuardContext.Provider value={value}>{children}</ModalGuardContext.Provider>;
}

export function useModalGuard() {
  const ctx = useContext(ModalGuardContext);
  if (!ctx) {
    // Soft fallback: if a component renders outside the provider (e.g. in a
    // standalone storybook view), return permissive no-ops rather than throwing.
    return {
      isAnyOpen: false,
      openCount: 0,
      register: () => {},
      unregister: () => {},
      guard: (cb) => { if (typeof cb === 'function') cb(); return true; },
    };
  }
  return ctx;
}

/**
 * Convenience hook for modal components — register self on mount when `isOpen`
 * is truthy, and unregister on close or unmount.
 */
export function useModalRegistration(modalId, isOpen) {
  const { register, unregister } = useModalGuard();
  useEffect(() => {
    if (!isOpen) return undefined;
    register(modalId);
    return () => unregister(modalId);
  }, [isOpen, modalId, register, unregister]);
}
